import React, { useEffect } from "react";
import { Row, Col } from "reactstrap";
import AdminLayout from "../../Layouts/AdminLayout";
import {
  initiateStripeOnboarding,
  checkStripeOnboardingStatus,
  getStripeExpressDashboardURL,
} from "../../Lib/RESTBlox";
import queryString from "query-string";
import Header from "../../Components/Header";
import { useLocation } from "react-router-dom";

type Props = {
  user: any;
};

const Dashboard: React.FC<Props> = ({ user }) => {
  const location = useLocation();

  const [isLoading, setIsLoading] = React.useState(false);
  const [statusMessage, setStatusMessage] = React.useState("");
  const [onboardingIncomplete, setOnboardingIncomplete] = React.useState(false);

  const handleStripeOnboarding = () => {
    setIsLoading(true);
    setStatusMessage("Setting up your Stripe account...");
    initiateStripeOnboarding()
      .then((json: any) => {
        console.log("Stripe onboarding response:", json);
        if (json.url) {
          // Redirect to Stripe's onboarding flow
          window.location.href = json.url;
        } else {
          setStatusMessage(json.message || "Error initiating Stripe onboarding");
          setIsLoading(false);
        }
      })
      .catch((err) => {
        console.error("Stripe onboarding error:", err);
        setStatusMessage("Error connecting to Stripe. Please try again.");
        setIsLoading(false);
      });
  };

  const handleExpressDashboardLogin = () => {
    getStripeExpressDashboardURL().then((json: any) => {
      console.log(json.url);
      window.location.href = json.url;
    });
  };

  const stripeButton = () => {
    // If onboarding is incomplete, show "Continue Setup" button
    if (onboardingIncomplete) {
      return (
        <button 
          className="btn btn-warning" 
          onClick={handleStripeOnboarding}
          disabled={isLoading}
        >
          {isLoading ? "Loading..." : "Continue Stripe Setup"}
        </button>
      );
    }
    
    if (user.profile.organization?.has_stripe_credentials) {
      return (
        <button
          className="btn btn-primary"
          onClick={handleExpressDashboardLogin}
        >
          Stripe Dashboard
        </button>
      );
    } else if (!user?.profile.organization?.is_the_blox_office) {
      return (
        <button 
          className="btn btn-primary" 
          onClick={handleStripeOnboarding}
          disabled={isLoading}
        >
          {isLoading ? "Loading..." : "Connect with Stripe"}
        </button>
      );
    } else {
      return "";
    }
  };

  useEffect(() => {
    let params = queryString.parse(location.search);
    
    // Handle return from Stripe onboarding
    if (params.stripe_return === "true") {
      setStatusMessage("Checking your Stripe account status...");
      checkStripeOnboardingStatus()
        .then((json: any) => {
          console.log("Stripe status check:", json);
          if (json.onboarding_complete) {
            setStatusMessage("Success! Your Stripe account is connected.");
            setOnboardingIncomplete(false);
            // Reload the page to update user data after a short delay
            setTimeout(() => {
              window.location.href = "/admin";
            }, 2000);
          } else {
            setStatusMessage(json.message || "Please complete your Stripe account setup.");
            setOnboardingIncomplete(true);
          }
        })
        .catch((err) => {
          console.error("Error checking Stripe status:", err);
          setStatusMessage("Unable to verify Stripe connection. Please try again.");
          setOnboardingIncomplete(true);
        });
    }
    
    // Handle refresh request from Stripe (user needs to restart onboarding)
    if (params.stripe_refresh === "true") {
      setStatusMessage("Your onboarding link expired. Click the button to continue setup.");
      setOnboardingIncomplete(true);
    }
    
    // Check onboarding status on page load if org has stripe_user_id but might be incomplete
    if (!params.stripe_return && !params.stripe_refresh && user.profile.organization?.has_stripe_credentials) {
      checkStripeOnboardingStatus()
        .then((json: any) => {
          if (!json.onboarding_complete) {
            setStatusMessage("Your Stripe setup is incomplete. Please continue setup below.");
            setOnboardingIncomplete(true);
          }
        })
        .catch(() => {
          // Silently fail - user can still try dashboard button
        });
    }
  }, [location.search, user.profile.organization?.has_stripe_credentials]);

  return (
    <AdminLayout title="Dashboard">
      <div className="BloxAdminContentContainer">
        <Row>
          <Col>
            {user.profile.organization?.is_the_blox_office ? (
              <p>
                You are logged into an account under The Blox Office's
                organization. Therefore, no need for Stripe stuff.
              </p>
            ) : (
              <div>
                <Header content="Let's get you set up with Stripe." />
                {user.profile.organization?.has_stripe_credentials ? (
                  <p>
                    The button below will connect you to the Stripe dashboard
                    for your account, where you can see your balance and
                    payouts.
                  </p>
                ) : (
                  <p>
                    The Blox Office uses Stripe for processing customer charges
                    and paying our clients. In order to get paid for your
                    transactions, you need to set up a Stripe account. Use the
                    button below to get started.
                  </p>
                )}
                {statusMessage && (
                  <div className="alert alert-info mb-3">
                    {statusMessage}
                  </div>
                )}
                {stripeButton()}
              </div>
            )}
          </Col>
        </Row>
      </div>
    </AdminLayout>
  );
};

export default Dashboard;
